/* assets/setup-builder.js
 * Viperia Setup Builder MVP: front-end module for the species setup pages.
 *
 * #90 (this file's first version): module scaffold only.
 *   - One SetupBuilder per [data-setup-builder] root (sections/species-setup-detail.liquid).
 *   - Reads and checks every tier's JSON (#89, [data-setup-tier] >
 *     script[data-setup-tier-data]) and the UI strings (script[data-setup-strings]).
 *   - Marks each tier data-setup-state="ready" or "error" (bad data never breaks
 *     the page; a tier with bad data will simply not be addable).
 *   - Fires "setup-builder:ready" on the root, and keeps the builder on
 *     root.setupBuilder for later tickets and for debugging in the console.
 *   - Makes NO network requests and changes nothing visible.
 * #91: Ajax Cart API payload (guide 8.2). planTier() turns one tier's JSON into
 *   the body for ONE POST to /cart/add.js: { items: [{ id, quantity }, ...] }.
 *   - Addable = status ok/fallback, available, valid variant id and quantity.
 *   - Optional items that are not addable are skipped (listed in plan.skipped).
 *   - A tier that is blocked, or has a required item that is not addable, gets
 *     no payload (status "blocked"); nothing addable gives status "empty".
 *     (Oct 2026: required items that are not addable are skipped too, see below.)
 *   - The same variant on two items of one tier is merged into one line with
 *     the quantities added up (one cart line either way once #93 adds the same
 *     properties to every line; merging keeps the request unambiguous).
 *   - Ids and quantities only: no prices, so retail and wholesale payloads are
 *     identical (BSS B2B reprices wholesale lines in the basket).
 *   Still pure data: nothing is sent and nothing on the page changes.
 * #92: add-all button ([data-setup-add], rendered disabled by setup-tiers.liquid).
 *   - Enabled only when planTier() is "ready"; blocked/empty/error tiers keep it
 *     disabled. Wholesale tiers are addable (BSS reprices in the basket).
 *   - One POST of the #91 payload to /cart/add.js.
 *   - Success: go to the basket page, exactly as the theme's own product form
 *     does when there is no cart drawer (cart_type "page"). Minimal version of
 *     #95/#97 so the flow is usable; #95-#97 refine it.
 *   - 422 (stock): Shopify adds what it can and reports the rest (checked live
 *     on 6 Oct 2026: the other lines were added and the short line was capped
 *     at stock), so a 422 means the basket has changed. Shopify's message is
 *     shown as text with a "View basket" link, and that tier's button stays
 *     disabled so a retry cannot add the same items twice. Other tiers stay
 *     addable: they are separate requests for other items.
 *   - Network error, timeout (20 s) or 5xx: the outcome is unknown (the add may
 *     still have gone through). Since the double-add fix this checks the basket
 *     first, see "Unknown add result" below. Other 4xx (e.g. 429): strings.error,
 *     re-enabled.
 *   - One request at a time per page (inFlight + disabled button), so a double
 *     click cannot add a tier twice: the minimum of #94, which adds the visible
 *     loading state.
 * #93: line item properties (guide 8.2, Q-10). Every line of a tier gets the same
 *   { Setup, Tier, _setup_handle, _tier_handle }: Setup = setup.title and Tier =
 *   tier.name are shown in the basket, at checkout and on the order; the
 *   underscore handles are hidden from shoppers but stay on the order. Values are
 *   strings, single-line, trimmed and length-capped; "<" and ">" become "‹" "›"
 *   because the theme's basket/order templates print property values unescaped.
 *   Blank values are left out. Lines with properties are separate from lines of
 *   the same variant added elsewhere (another tier, a product page); adding the
 *   same tier again adds to its own lines.
 * #94: loading state and duplicate-click prevention (guide 8.3, UJ-04).
 *   - While its request is out, the pressed button shows the theme's spinner
 *     (snippets/loading-spinner.liquid) and strings.adding, with aria-busy and
 *     aria-disabled; the "Adding…" text also goes to the tier's status region
 *     (screen readers only). The label and busy content share one grid cell, so
 *     the button keeps its width.
 *   - The button keeps focus (aria-disabled, not disabled) and ignores clicks,
 *     as the theme's own product form does. The page lock (inFlight) still
 *     allows one request at a time across all tiers; other tiers' buttons are
 *     left as they are (the guide asks for the lock, not for disabling them).
 *   - Every error path (422, other 4xx, 5xx, network error, timeout) clears the
 *     busy state and the lock; a 422 then locks that tier as in #92 and moves
 *     focus to "View basket" if the button had it.
 *   - Success stays locked until the basket page loads (no enabled flash; #95
 *     swaps the spinner for the "Added" state); if the browser restores this page
 *     from the back/forward cache, the busy / added state is cleared (pageshow).
 * #95: success handling (guide 8.4, Q-12, UJ-08). onSuccess() after a 2xx:
 *   - Fires "setup-builder:added" (bubbles from the tier element) with
 *     { setup, tier, items (Shopify's response lines), skipped (optional items
 *     left out), payload }, so the theme or analytics can hook in later.
 *   - Success text = strings.added, plus strings.skippedNote when optional
 *     items were left out (UJ-08).
 *   - Post-add experience (Q-12 "drawer if supported, else /cart"): stay on the
 *     page only if cart mode is "auto" AND refreshCartUI() (#96) opened the
 *     theme's cart drawer / notification. This theme uses cart_type "page" (no
 *     drawer) and the section sets no cart mode, so the shopper goes to the
 *     basket page, as with the theme's own product form.
 *   - Going to the basket: the pressed button changes from the spinner to a tick
 *     + strings.addedLabel ("Added") in the same grid cell (no size change, no
 *     layout shift), stays aria-disabled and locked, and keeps focus; the
 *     success text goes to the status region for screen readers only (a visible
 *     line would push the page down just before it changes). Then navigate.
 *   - Staying (drawer / notification): the button is reset and the status
 *     region shows the success text visibly with a "View basket" link; the lock
 *     is released.
 * #96: cart count and drawer refresh (guide 8.5, Q-11). Checked against the
 *   theme (6 Oct 2026, MAIN = DEV): cart_type "page", so no <cart-drawer> or
 *   <cart-notification> is rendered; sections/header.liquid prints the count
 *   itself inside <span id="cart-icon-bubble"> as
 *   <span class="cart-count-bubble" aria-hidden="true">N</span>
 *   <span class="visually-hidden">N items</span> (nothing when the basket is empty).
 *   - Header count, refreshCartCount(): one GET of the Section Rendering API
 *     (?sections=cart-icon-bubble, the theme's own cart-icon-bubble section),
 *     then the count and its hidden label (both as text) are written into
 *     #cart-icon-bubble in the header's own markup. Used only when the page
 *     stays and the basket may have changed: after a 422 (partial add) and
 *     after a network error / timeout / 5xx (the add may have gone through),
 *     so the header matches the "View basket" message; and when the browser
 *     restores this page from the back/forward cache after an add (the header
 *     would otherwise show the count from before the add). Not before going to
 *     the basket page, which renders the right count itself. If it fails, the
 *     header is left as it was (the basket page is always right).
 *   - Drawer / notification, refreshCartUI(body): cart mode "auto" only. If the
 *     theme renders Dawn's <cart-notification> or <cart-drawer> (cart_type
 *     "notification" / "drawer"), the add request asks for the sections that
 *     element renders (its getSectionsToRender(), sections_url = this page) and
 *     the response is handed to its renderContents(), which updates the count
 *     and opens it, exactly as the theme's product form does. Otherwise false,
 *     and the shopper goes to the basket page (#97 fallback). Dormant on this
 *     theme today (no cart mode set, cart_type "page").
 * #97: fallback to the basket page (guide 8.6). The basket page is the fallback
 *   whenever the drawer path does not complete: cart mode "redirect" (section
 *   setting "After add to basket"), no drawer / notification, no sections in
 *   the response, or an error in the theme's drawer code (#95/#96). The items
 *   are already in the basket then, so the basket page is always right. URL =
 *   data-cart-url (routes.cart_url, so it follows the shop's locale), else
 *   window.routes.cart_url, else /cart.
 *   - If the basket page does not open (navigation blocked or cancelled, e.g.
 *     the shopper pressed Esc / Stop, or still loading after
 *     NAVIGATE_FALLBACK_MS), the "Added" button stays as it is (no second add)
 *     and the status region shows the success text visibly with a "View basket"
 *     link, the page lock is released and the header count is refreshed (#96).
 *     The timer stops when the page is left (pagehide) or restored (pageshow).
 * BSS B2B wholesale prices (wholesale customers only; retail is untouched).
 *   - snippets/setup-tiers.liquid gives every priced wholesale row a BSS
 *     featured product form with one price node [data-setup-bss-price]
 *     (bss-b2b-product-id / -variant-id / -product-price / -variant-price) that
 *     holds the base ex-VAT unit price. The BSS app prices it in the browser.
 *   - BSS's finished signal per node: it sets an inline visibility: visible
 *     after writing (its stylesheet hides [bss-b2b-product-price] until then).
 *     Its content is then either the pair <span>struck base</span><span>wholesale
 *     </span> (item in a price list) or the unchanged base price (in no price
 *     list, which is also what checkout charges). The last <span> is the price.
 *   - A row price is shown only once its node is finished. Line price = unit x
 *     quantity (shown for quantity > 1). Tier totals = the same items and
 *     required / optional split as retail (rows with status ok / fallback), and
 *     are shown only when ALL of those rows are finished; the totals appear and
 *     the wholesale note disappears in the same step, so a total is never shown
 *     half updated. The "ex VAT" label appears once any row price is shown.
 *   - A MutationObserver on the price nodes keeps everything in step if BSS
 *     writes again. If BSS has not finished within WHOLESALE_TIMEOUT_MS the
 *     tier is marked data-setup-prices="fallback": unfinished prices stay hidden
 *     (as on the product page) and the wholesale note stays. Prices that arrive
 *     later are still shown.
 *   - Reads only what BSS writes into its own markup; never BSS's internal
 *     settings object. Makes no requests.
 * Unknown add result (double-add fix, follows #92/#94). After a network error,
 *   the 20 s timeout or a 5xx the add may still have gone through, and a plain
 *   retry could add the whole tier twice. So before the button is usable again,
 *   basketHas() reads the basket (GET cart.js, VERIFY_TIMEOUT_MS) and compares
 *   the lines tagged with this tier's _tier_handle and _setup_handle (#93) with
 *   the payload, per variant:
 *   - all there: treated as added. "Added" tick on the button, strings.added
 *     (+ skippedNote) with a visible "View basket" link, tier locked (as after
 *     a 422), header count refreshed, "setup-builder:added" fired with
 *     recovered: true. No automatic redirect (the shopper has just seen a
 *     delay; the link is the next step).
 *   - some there: strings.partialAdded + "View basket", tier locked, count
 *     refreshed (as a 422).
 *   - none there after a network error or 5xx: nothing was added, so
 *     strings.error and the button is re-enabled (no link needed).
 *   - none there after a timeout (the aborted request can still be processed
 *     by Shopify later), or the basket could not be read: strings.unconfirmed
 *     ("check your basket before trying again") + "View basket", count
 *     refreshed, button re-enabled, and the tier remembers it (tier.uncertain).
 *     The next press checks the basket again BEFORE posting: if the lines are
 *     there by then it is treated as added / partial as above and nothing is
 *     posted; if not (or the basket still cannot be read, after the warning was
 *     shown) the add goes ahead.
 *   Limitation: lines of the same tier added earlier (another visit) look the
 *   same as this add, so a repeat add of the same tier whose result is unknown
 *   is reported as "in your basket" rather than retried. The wording stays
 *   true (those items are in the basket) and the basket page shows quantities.
 * #99 / #100 catalogue guards. The tier JSON is checked before anything is
 *   enabled (validateTier: shape, statuses, integer quantity >= 1, an id on every
 *   addable item, no prices on wholesale tiers); a broken tier keeps its button
 *   disabled and the rest of the page works. Items that are unavailable or
 *   invalid (product deleted, draft or not on the Online Store, see
 *   setup-tiers.liquid) are left out of the add (isAddable; Oct 2026: required
 *   ones too, they no longer block). A missing or unparseable JSON block
 *   disables only that tier.
 * #100 product removed after the page was rendered. Shopify rejects the add with
 *   404 {"status":404,"message":"Cart Error","description":"Cannot find variant"}
 *   (Cart API reference; 422 with the same description is also reported). This
 *   is not a temporary error, so retrying would fail again: itemsChanged()
 *   shows strings.itemsChanged ("... no longer available. Please refresh the
 *   page ..."), keeps the tier disabled and, as for an unknown result, reads the
 *   basket (basketHas) so a partial add is never hidden: if any of the tier's
 *   lines are there, a "View basket" link is added and the header count
 *   refreshed. A refresh renders the tier with the current catalogue
 *   (placeholder row or item hidden).
 * Figma redesign (Oct 2026): popular extras. snippets/setup-extras.liquid renders
 *   a checkbox [data-setup-extra] (data-variant-id) per available extra product
 *   inside this root. withExtras() appends every ticked extra (quantity 1, same
 *   line item properties as the tier) to the ready plan of the tier being added,
 *   merging into a tier line of the same variant. It runs only at the moment the
 *   button is pressed, so with nothing ticked the request is exactly the tier's
 *   (planTier() itself is unchanged and stays pure). Because the extras are in
 *   plan.payload with the tier's properties, the double-add basket check
 *   (basketHas), partial-add / 422 / itemsChanged handling and the
 *   "setup-builder:added" event cover them like any tier line. plan.extras lists
 *   them. Unchecked, disabled or invalid ids are ignored.
 * Oct 2026 (Harry, 8 Oct): sold-out items are skipped, never block a tier, and
 *   optional items can be left out.
 *   - partitionItems / planTier: every item that is not addable (sold out, short
 *     of stock, missing product; required or optional) goes to plan.skipped and
 *     the rest is added. Status "blocked" only remains for tier JSON that still
 *     says blocked: true (Liquid always prints false now); "empty" (nothing
 *     addable) keeps the button disabled.
 *   - Optional toggles: snippets/setup-tiers.liquid renders a disabled, ticked
 *     checkbox [data-setup-optional][data-item-index] on each addable optional
 *     row (drawn as the row's own dot; clicking the row image toggles it too).
 *     bindTier enables them for an addable tier; readToggles() turns the
 *     unticked ones into tier.excluded (indexes into the tier JSON items[], only
 *     honoured for optional addable items), so planTier leaves them out
 *     (plan.excluded lists them). The payload, and therefore the double-add
 *     basket check, the 422 / partial-add / itemsChanged handling and the
 *     "setup-builder:added" event, use exactly the items sent. A change
 *     re-checks the plan: nothing left -> button disabled; fires
 *     "setup-builder:change" on the tier. Toggles are disabled while the tier is
 *     busy and once it is locked. Wholesale (BSS) totals leave unticked rows out;
 *     retail totals and the tier card are updated by assets/setup-builder-ui.js.
 *   - Without JavaScript the toggles stay ticked and disabled and the button
 *     stays disabled (as before).
 * Wholesale tier JSON (priceBasis "wholesale_hidden") still carries no prices
 * (#88/#89): the add payload is ids and quantities only.
 *
 * No dependencies, nothing added to window. Loaded with defer by the section.
 */
(() => {
  'use strict';

  const LOG = '[setup-builder]';
  const STATUSES = new Set(['ok', 'fallback', 'unavailable', 'invalid']);
  const ADDABLE = new Set(['ok', 'fallback']);
  const PRICE_BASES = new Set(['inc_vat', 'wholesale_hidden']);
  const PRICE_KEYS = ['subtotal', 'requiredSubtotal', 'price', 'linePrice'];
  const DATA_VERSION = 1;
  const REQUEST_TIMEOUT_MS = 20000;
  // #96 header cart count: the theme's section and the element it fills.
  const CART_COUNT_SECTION = 'cart-icon-bubble';
  const COUNT_TIMEOUT_MS = 10000;
  // #97 if the basket page has not opened by then, show the "View basket" link.
  const NAVIGATE_FALLBACK_MS = 8000;
  // Double-add fix: how long to wait for cart.js when checking an unknown add result.
  const VERIFY_TIMEOUT_MS = 8000;
  // BSS wholesale prices: how long to wait for BSS before settling on the fallback.
  const WHOLESALE_TIMEOUT_MS = 8000;
  // #93 property limits: visible text (setup titles are max 80 in the admin) and handles.
  const MAX_PROPERTY_TEXT = 120;
  const MAX_PROPERTY_HANDLE = 255;

  // English fallbacks, used only if the strings block is missing or incomplete.
  const DEFAULT_STRINGS = {
    adding: 'Adding…',
    added: 'Added to your basket.',
    addedLabel: 'Added',
    skippedNote: 'Out-of-stock items were not added.',
    viewBasket: 'View basket',
    requiredUnavailable: "A required item is out of stock, so this tier can't be added at the moment.",
    nothingToAdd: "There's nothing available to add from this tier right now.",
    error: "Sorry, we couldn't add these items. Please try again.",
    partialAdded: 'Some of these items were added to your basket. Please check your basket.',
    unconfirmed: "We couldn't confirm whether these items were added. Please check your basket before trying again.",
    itemsChanged: 'Some items in this setup are no longer available. Please refresh the page to see the latest setup.',
  };

  const readJSON = (el) => {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.warn(`${LOG} Invalid JSON`, err);
      return null;
    }
  };

  const isId = (v) => Number.isInteger(v) && v > 0;

  // #100 Shopify's answer when a variant was deleted or is no longer on the Online
  // Store (see header). A 404 counts only with Shopify's JSON error body, so a
  // plain 404 page (e.g. a wrong URL) stays a generic error.
  const MISSING_VARIANT = /cannot find variant/i;
  function missingVariant(status, body) {
    const description = body && typeof body.description === 'string' ? body.description : '';
    if (MISSING_VARIANT.test(description)) return true;
    return status === 404 && !!body && Number(body.status) === 404;
  }
  const hasOwn = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

  // Checks one tier block against the #89 shape. Returns a list of problems;
  // an empty list means the tier is usable.
  function validateTier(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return ['missing or invalid JSON'];
    const problems = [];
    if (data.version !== DATA_VERSION) problems.push(`version ${data.version}`);
    if (!data.setup || typeof data.setup.handle !== 'string') problems.push('setup');
    if (!data.tier || typeof data.tier.handle !== 'string' || typeof data.tier.name !== 'string') problems.push('tier');
    if (typeof data.blocked !== 'boolean') problems.push('blocked');
    if (!PRICE_BASES.has(data.priceBasis)) problems.push('priceBasis');
    if (!Array.isArray(data.items)) {
      problems.push('items');
      return problems;
    }
    data.items.forEach((item, n) => {
      const at = `items[${n}]`;
      if (!item || typeof item !== 'object') return problems.push(at);
      if (!STATUSES.has(item.status)) problems.push(`${at}.status`);
      if (!Number.isInteger(item.quantity) || item.quantity < 1) problems.push(`${at}.quantity`);
      if (typeof item.required !== 'boolean') problems.push(`${at}.required`);
      if (ADDABLE.has(item.status) && !isId(item.variantId)) problems.push(`${at}.variantId`);
      return null;
    });
    // Wholesale prices come from BSS B2B, never from Liquid: raw prices must not be here.
    if (data.priceBasis === 'wholesale_hidden') {
      const leaked = PRICE_KEYS.some((k) => hasOwn(data, k) || data.items.some((i) => i && hasOwn(i, k)));
      if (leaked) problems.push('price data on a wholesale tier');
    }
    return problems;
  }

  const isAddable = (item) => ADDABLE.has(item.status) && item.available !== false
    && isId(item.variantId) && Number.isInteger(item.quantity) && item.quantity > 0;

  // #91 Decide what can be added (guide 8.2 partitionItems). Pure: never
  // modifies the tier data. Required items default to required.
  // Oct 2026: items that are not addable are skipped (required or optional) and
  // `excluded` (a Set of items[] indexes, from the optional toggles) leaves out
  // optional addable items; required items can never be excluded.
  function partitionItems(data, excluded) {
    const items = Array.isArray(data.items) ? data.items : [];
    const off = excluded instanceof Set ? excluded : new Set();
    const isOff = (item, n) => item.required === false && off.has(n);
    return {
      requiredMissing: items.some((i) => i.required !== false && !isAddable(i)),
      addable: items.filter((i, n) => isAddable(i) && !isOff(i, n)),
      skipped: items.filter((i) => !isAddable(i)),
      excluded: items.filter((i, n) => isAddable(i) && isOff(i, n)),
    };
  }

  // #93 One clean single-line string: no control characters or line breaks,
  // "<"/">" swapped for look-alikes (the basket prints values unescaped),
  // whitespace collapsed, capped at `max` characters (not splitting emoji).
  function propertyText(value, max) {
    const text = String(value == null ? '' : value)
      .replace(/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/g, ' ')
      .replace(/</g, '\u2039')
      .replace(/>/g, '\u203a')
      .replace(/\s+/g, ' ')
      .trim();
    const chars = Array.from(text);
    return chars.length > max ? `${chars.slice(0, max - 1).join('').trimEnd()}\u2026` : text;
  }

  // #93 Line item properties shared by every line of a tier (guide 8.2).
  function lineProperties(data) {
    const setup = data.setup || {};
    const tier = data.tier || {};
    const props = {
      Setup: propertyText(setup.title, MAX_PROPERTY_TEXT) || propertyText(setup.handle, MAX_PROPERTY_TEXT),
      Tier: propertyText(tier.name, MAX_PROPERTY_TEXT) || propertyText(tier.handle, MAX_PROPERTY_TEXT),
      _setup_handle: propertyText(setup.handle, MAX_PROPERTY_HANDLE),
      _tier_handle: propertyText(tier.handle, MAX_PROPERTY_HANDLE),
    };
    Object.keys(props).forEach((k) => {
      if (!props[k]) delete props[k];
    });
    return props;
  }

  // #91 Ajax Cart API body for one tier: all lines in a single request.
  // Lines keep display order; a repeated variant is merged into its first line
  // (same variant + same properties = one basket line anyway).
  // #93 adds the tier's properties to each line; #96 adds sections / sections_url.
  function buildPayload(addable, properties) {
    const lines = new Map();
    addable.forEach((item) => {
      const line = lines.get(item.variantId);
      if (line) line.quantity += item.quantity;
      else lines.set(item.variantId, { id: item.variantId, quantity: item.quantity, properties: { ...properties } });
    });
    return { items: Array.from(lines.values()) };
  }

  // SUP-32 shopper quantities: `quantities` (Map of items[] index -> integer >= 1,
  // from the row quantity selectors) replaces the setup quantity. Pure: returns
  // a copy, the rendered tier data is never changed.
  function withQuantities(data, quantities, variants) {
    const hasQ = quantities instanceof Map && quantities.size;
    const hasV = variants instanceof Map && variants.size;
    if ((!hasQ && !hasV) || !Array.isArray(data.items)) return data;
    return {
      ...data,
      items: data.items.map((item, n) => {
        let out = item;
        const q = hasQ ? quantities.get(n) : undefined;
        if (Number.isInteger(q) && q >= 1) out = { ...out, quantity: q };
        const v = hasV ? variants.get(n) : undefined;
        if (v && isId(v.id) && ADDABLE.has(out.status)) out = { ...out, variantId: v.id, variantTitle: v.title || out.variantTitle, available: true };
        return out;
      }),
    };
  }

  // SUP-32 a quantity input's value as a whole number within its min / max.
  function clampQty(input) {
    const min = Math.max(1, parseInt(input.min, 10) || 1);
    const max = Math.max(min, parseInt(input.max, 10) || 99);
    const n = parseInt(input.value, 10);
    if (!Number.isInteger(n)) return min;
    return Math.min(max, Math.max(min, n));
  }

  // #91 Everything #92 needs to add one tier, as plain data.
  //   status  "ready" (payload set) | "blocked" | "empty" | "error"
  //   payload { items: [{ id, quantity, properties }] } or null
  //   properties  the #93 line item properties used on every line
  //   addable / skipped  the tier items behind the payload / left out because
  //           they can't be added (sold out etc., required or optional)
  //   excluded  optional items the shopper unticked (tier.excluded)
  //   merged  number of items folded into an earlier line with the same variant
  function planTier(tier) {
    if (!tier || !tier.ok) return { status: 'error', payload: null, properties: null, addable: [], skipped: [], excluded: [], merged: 0 };
    const { addable, skipped, excluded } = partitionItems(withQuantities(tier.data, tier.quantities, tier.variants), tier.excluded);
    const properties = lineProperties(tier.data);
    const base = { addable, skipped, excluded, merged: 0, properties };
    // Only tier JSON that says so is blocked (Liquid prints false since Oct 2026).
    if (tier.data.blocked) return { ...base, status: 'blocked', payload: null };
    if (!addable.length) return { ...base, status: 'empty', payload: null };
    const payload = buildPayload(addable, properties);
    return { ...base, status: 'ready', payload, merged: addable.length - payload.items.length };
  }

  // ---- BSS wholesale prices ----

  // Integer pence from a money string written by BSS (or Liquid) in the shop's
  // money format: digits only, x 100 for the "no_decimals" formats.
  function parseMoney(text, format) {
    const digits = String(text == null ? '' : text).replace(/[^\d]/g, '');
    if (!digits) return null;
    const value = parseInt(digits, 10);
    if (!Number.isSafeInteger(value)) return null;
    return /no_decimals/.test(format || '') ? value * 100 : value;
  }

  // Pence -> text in the shop's money format (Shopify placeholders), as the
  // theme's | money filter prints it; Intl fallback if no format is given.
  function formatMoney(cents, format, currency) {
    const fmt = String(format || '').replace(/<[^>]*>/g, '');
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: currency || 'GBP' }).format(cents / 100);
      } catch (err) {
        return (cents / 100).toFixed(2);
      }
    }
    const group = (decimals, thousands, point) => {
      const [whole, frac] = (cents / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? point + frac : '');
    };
    const amounts = {
      amount: () => group(2, ',', '.'),
      amount_no_decimals: () => group(0, ',', '.'),
      amount_with_comma_separator: () => group(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => group(0, '.', ','),
      amount_with_apostrophe_separator: () => group(2, "'", '.'),
      amount_with_space_separator: () => group(2, ' ', ','),
      amount_no_decimals_with_space_separator: () => group(0, ' ', ''),
    };
    return fmt.replace(match[0], (amounts[match[1]] || amounts.amount)());
  }

  // One BSS price node -> { done, unit } (unit in pence, or null).
  // done: BSS has set its inline visibility: visible (it does so after writing).
  function readBssPrice(node, format) {
    if (!node || node.style.visibility !== 'visible') return { done: false, unit: null };
    const spans = node.querySelectorAll(':scope > span');
    const priceEl = spans.length ? spans[spans.length - 1] : node;
    const unit = parseMoney(priceEl.textContent, format);
    return { done: unit !== null, unit };
  }

  class SetupBuilder {
    constructor(root) {
      this.root = root;
      const { dataset } = root;
      const routes = window.routes || {};
      const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      // Cart settings for #92-#97. cart_type is "page" on this theme, so the
      // default after adding is to go to the basket page.
      this.cartMode = dataset.cartMode === 'auto' ? 'auto' : 'redirect';
      this.sectionIds = (dataset.cartSections || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.cartUrl = dataset.cartUrl || routes.cart_url || `${shopRoot}cart`;
      this.cartAddUrl = `${dataset.cartAddUrl || routes.cart_add_url || `${shopRoot}cart/add`}.js`;

      this.strings = { ...DEFAULT_STRINGS, ...(readJSON(root.querySelector('[data-setup-strings]')) || {}) };
      this.inFlight = false; // page-level lock: one cart request at a time (#92; visible busy state #94)
      this.cartChanged = false; // #96 this page has (or may have) changed the basket
      this.countRequest = null; // #96 header count request in progress
      this.navTimer = null; // #97 basket page fallback timer

      this.tiers = Array.from(root.querySelectorAll('[data-setup-tier]'), (el) => this.readTier(el));
      this.watchWholesalePrices();

      root.dataset.setupState = 'ready';
      root.dispatchEvent(new CustomEvent('setup-builder:ready', { bubbles: true, detail: { builder: this } }));
    }

    // Reads one tier's JSON once and binds its add button (#92).
    readTier(el) {
      const data = readJSON(el.querySelector('[data-setup-tier-data]'));
      const problems = validateTier(data);
      const ok = problems.length === 0;
      if (!ok) console.warn(`${LOG} tier data ignored`, el.dataset.tierHandle || el.id || '', problems);
      el.dataset.setupState = ok ? 'ready' : 'error';
      const tier = { el, ok, problems, data: ok ? data : null, button: el.querySelector('[data-setup-add]'), busy: false, locked: false, uncertain: false };
      this.bindTier(tier);
      return tier;
    }

    // #92 Enable the button only for a tier that can be added.
    bindTier(tier) {
      const { button } = tier;
      if (!button || button.dataset.setupBound) return;
      button.dataset.setupBound = 'true';
      // #94 busy text comes from the strings block (shown only while adding).
      const busyText = button.querySelector('[data-setup-add-busy-text]');
      if (busyText) busyText.textContent = this.strings.adding;
      // #95 "Added" text for the success state (shown only while going to the basket).
      const doneText = button.querySelector('[data-setup-add-done-text]');
      if (doneText) doneText.textContent = this.strings.addedLabel;
      // Addable with every optional item ticked? Otherwise nothing to bind.
      if (planTier({ ...tier, excluded: new Set() }).status !== 'ready') {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        return;
      }
      button.addEventListener('click', () => this.addTier(tier));
      // Oct 2026 optional toggles (rendered disabled): enable and follow them.
      tier.toggles = Array.from(tier.el.querySelectorAll('input[data-setup-optional]'));
      tier.toggles.forEach((el) => {
        el.disabled = false;
        el.addEventListener('change', () => this.onToggle(tier));
        // The row's dot is the checkbox; its image toggles it too (the title
        // stays a link to the product). Ignored while the tier is busy / locked.
        const row = el.closest('.setup-item');
        const media = row && row.querySelector('.setup-item__media');
        if (media) media.addEventListener('click', () => { if (!el.disabled) el.click(); });
      });
      // SUP-32 quantity selectors (rendered disabled): enable and follow them.
      tier.qtyControls = Array.from(tier.el.querySelectorAll('[data-setup-qty], [data-setup-qty-step]'));
      tier.el.querySelectorAll('input[data-setup-qty]').forEach((input) => {
        const commit = () => {
          input.value = String(clampQty(input));
          this.onToggle(tier);
        };
        input.addEventListener('change', commit);
        input.addEventListener('input', () => {
          if (input.value !== '' && parseInt(input.value, 10) >= 1) this.onToggle(tier);
        });
        const wrap = input.closest('.setup-item__qty');
        if (!wrap) return;
        wrap.querySelectorAll('[data-setup-qty-step]').forEach((btn) => {
          btn.addEventListener('click', () => {
            if (input.disabled) return;
            input.value = String(clampQty(input) + (parseInt(btn.dataset.setupQtyStep, 10) || 0));
            commit();
          });
        });
      });
      // TKT-1400 colour selects (rendered disabled): enabled with the qty controls.
      tier.variantSelects = Array.from(tier.el.querySelectorAll('select[data-setup-variant]'));
      tier.qtyControls = (tier.qtyControls || []).concat(tier.variantSelects);
      tier.variantSelects.forEach((sel) => sel.addEventListener('change', () => this.onVariant(tier, sel)));
      // SUP-32 Door Type / Colour option selects (rendered disabled): same switch.
      tier.optionSelects = Array.from(tier.el.querySelectorAll('select[data-setup-option]'));
      tier.qtyControls = tier.qtyControls.concat(tier.optionSelects);
      tier.optionSelects.forEach((sel) => sel.addEventListener('change', () => this.onOption(sel)));
      tier.el.querySelectorAll('[data-setup-choices]').forEach((el) => this.syncChoices(el.closest('.setup-item')));
      this.setToggles(tier, true);
      // A restored page can come back with boxes unticked.
      this.onToggle(tier, true);
    }

    // Oct 2026: unticked optional toggles -> tier.excluded (items[] indexes).
    readToggles(tier) {
      const set = new Set();
      if (tier && tier.el) {
        tier.el.querySelectorAll('input[data-setup-optional]').forEach((el) => {
          const n = parseInt(el.dataset.itemIndex, 10);
          if (!el.checked && Number.isInteger(n) && n >= 0) set.add(n);
        });
        tier.excluded = set;
        // SUP-32 quantities: read every selector, keep the row's data-quantity
        // (used by the wholesale totals) and its min / max buttons in step.
        const quantities = new Map();
        tier.el.querySelectorAll('input[data-setup-qty]').forEach((input) => {
          const n = parseInt(input.dataset.itemIndex, 10);
          if (!Number.isInteger(n) || n < 0) return;
          const q = clampQty(input);
          quantities.set(n, q);
          const row = input.closest('.setup-item');
          if (row) row.dataset.quantity = String(q);
          const wrap = input.closest('.setup-item__qty');
          if (wrap && !input.disabled) {
            wrap.querySelectorAll('[data-setup-qty-step]').forEach((btn) => {
              const step = parseInt(btn.dataset.setupQtyStep, 10) || 0;
              btn.disabled = step < 0 ? q <= Math.max(1, parseInt(input.min, 10) || 1) : q >= (parseInt(input.max, 10) || 99);
            });
          }
        });
        const variants = new Map();
        tier.el.querySelectorAll('select[data-setup-variant]').forEach((sel) => {
          const n = parseInt(sel.dataset.itemIndex, 10);
          const opt = sel.selectedOptions[0];
          const id = parseInt(sel.value, 10);
          if (Number.isInteger(n) && n >= 0 && isId(id) && opt && !opt.disabled) variants.set(n, { id, title: opt.dataset.title || '' });
        });
        tier.variants = variants;
        tier.quantities = quantities;
      }
      return set;
    }

    // TKT-1400 colour picked: row data follows the option, then totals/plan redo.
    onVariant(tier, sel) {
      const row = sel.closest('.setup-item');
      const opt = sel.selectedOptions[0];
      if (!row || !opt) return;
      row.dataset.variantId = opt.value;
      if (opt.dataset.unitPrice !== undefined) row.dataset.unitPrice = opt.dataset.unitPrice;
      if (opt.dataset.unitRrp !== undefined) row.dataset.unitRrp = opt.dataset.unitRrp;
      const qty = row.querySelector('input[data-setup-qty]');
      if (qty) {
        qty.max = String(Math.max(1, parseInt(opt.dataset.max, 10) || 99));
        qty.value = String(clampQty(qty));
      }
      const img = row.querySelector('.setup-item__image');
      if (img && opt.dataset.image) { img.removeAttribute('srcset'); img.src = opt.dataset.image; }
      const titleLink = row.querySelector('a.setup-item__title');
      if (titleLink && titleLink.getAttribute('href')) {
        try { const u = new URL(titleLink.getAttribute('href'), window.location.origin); u.searchParams.set('variant', opt.value); titleLink.setAttribute('href', u.pathname + u.search + u.hash); } catch (e) { /* keep original link */ }
      }
      row.querySelectorAll('[data-setup-wholesale-price][data-variant-id]').forEach((w) => { w.hidden = w.dataset.variantId !== opt.value; });
      this.onToggle(tier);
    }

    // SUP-32 Colour + Door Type: one select per option (data-setup-option) picks
    // the matching in-stock variant in the row's hidden variant select, then fires
    // its change so onVariant / the totals run exactly as for a colour pick.
    onOption(sel) {
      const row = sel.closest('.setup-item');
      const variantSel = row && row.querySelector('select[data-setup-variant]');
      if (!variantSel) return;
      const pos = sel.dataset.setupOption;
      const picks = Array.from(row.querySelectorAll('select[data-setup-option]'), (s) => [s.dataset.setupOption, s.value]);
      const opts = Array.from(variantSel.options).filter((o) => !o.disabled);
      // Exact combination first; else keep the value just picked and move the others.
      const match = opts.find((o) => picks.every(([p, v]) => o.dataset[`o${p}`] === v))
        || opts.find((o) => o.dataset[`o${pos}`] === sel.value);
      if (match && variantSel.value !== match.value) {
        variantSel.value = match.value;
        variantSel.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.syncChoices(row);
    }

    // SUP-32 option selects follow the chosen variant; a value with no in-stock
    // variant at all is shown as sold out and can't be picked.
    syncChoices(row) {
      const variantSel = row && row.querySelector('select[data-setup-variant]');
      const current = variantSel && variantSel.selectedOptions[0];
      if (!current) return;
      const opts = Array.from(variantSel.options);
      row.querySelectorAll('select[data-setup-option]').forEach((s) => {
        const key = `o${s.dataset.setupOption}`;
        const soldOut = s.dataset.soldOut || 'Sold out';
        Array.from(s.options).forEach((o) => {
          const exists = opts.some((v) => v.dataset[key] === o.value);
          const inStock = opts.some((v) => !v.disabled && v.dataset[key] === o.value);
          o.hidden = !exists;
          o.disabled = !inStock;
          o.textContent = (o.dataset.label || o.value) + (exists && !inStock ? ` – ${soldOut}` : '');
        });
        if (current.dataset[key] !== undefined) s.value = current.dataset[key];
      });
    }

    // Oct 2026: a toggle changed. Button follows the plan (nothing left ->
    // disabled), wholesale totals are redone, and the change is announced.
    onToggle(tier, quiet) {
      this.readToggles(tier);
      const { button } = tier;
      if (button && !tier.busy && !tier.locked) {
        const ready = planTier(tier).status === 'ready';
        button.disabled = !ready;
        if (ready) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', 'true');
      }
      if (this.wholesaleTiers && this.wholesaleTiers.includes(tier.el)) this.updateWholesaleTier(tier.el);
      if (quiet) return;
      tier.el.dispatchEvent(new CustomEvent('setup-builder:change', {
        bubbles: true,
        detail: { tier: tier.data ? tier.data.tier.handle : null, excluded: Array.from(tier.excluded || []) },
      }));
    }

    // Oct 2026: toggles follow the tier (off while busy or once locked).
    setToggles(tier, on) {
      (tier.toggles || []).forEach((el) => {
        el.disabled = !on;
      });
      // SUP-32 quantity selectors follow the toggles (readToggles sets the
      // +/- buttons' min / max state once they are on).
      (tier.qtyControls || []).forEach((el) => {
        el.disabled = !on;
      });
      if (on) this.readToggles(tier);
    }

    // #92 Add the whole tier in one /cart/add.js request.
    async addTier(tier) {
      const { button } = tier;
      // Ignore repeat clicks: page lock, busy (aria-disabled keeps focus, so the
      // button is not disabled while busy), blocked or locked after a 422.
      if (this.inFlight || !button || button.disabled || tier.busy || tier.locked) return;
      this.readToggles(tier);
      const plan = this.withExtras(planTier(tier));
      if (plan.status !== 'ready') {
        this.message(tier, plan.status === 'blocked' ? this.strings.requiredUnavailable : this.strings.nothingToAdd, 'error');
        return;
      }
      this.inFlight = true;
      this.setBusy(tier, true);
      this.message(tier, this.strings.adding, 'busy');
      // Double-add fix: the last add of this tier was not confirmed, so look in
      // the basket before posting again (see header).
      if (tier.uncertain) {
        const found = await this.basketHas(plan);
        if (found === 'all' || found === 'some') {
          this.recovered(tier, plan, found);
          return;
        }
      }
      let res;
      let body = {};
      try {
        res = await this.post(this.requestBody(plan.payload));
        body = await res.json().catch(() => ({}));
      } catch (err) {
        this.unknownResult(tier, plan, err);
        return;
      }
      if (res.status >= 500) {
        this.unknownResult(tier, plan, new Error(`HTTP ${res.status}`), res.status);
        return;
      }
      if (!res.ok && missingVariant(res.status, body)) {
        this.itemsChanged(tier, plan, res.status);
        return;
      }
      if (!res.ok) {
        this.fail(tier, new Error(`HTTP ${res.status}`), res.status, body);
        return;
      }
      tier.uncertain = false;
      this.onSuccess(tier, plan, body);
    }

    // Double-add fix: network error, timeout or 5xx. Check the basket before the
    // button can be used again (see header). Busy state and lock stay meanwhile.
    async unknownResult(tier, plan, err, status) {
      console.warn(`${LOG} add result unknown, checking the basket`, (tier.data && tier.data.tier.handle) || '', status || '', err);
      const timedOut = !!err && err.name === 'AbortError';
      const found = await this.basketHas(plan);
      if (found === 'all' || found === 'some') {
        this.recovered(tier, plan, found);
        return;
      }
      this.setBusy(tier, false);
      this.inFlight = false;
      if (found === 'none' && !timedOut) {
        // Shopify answered or the connection failed, and the basket has none of it.
        tier.uncertain = false;
        this.message(tier, this.strings.error, 'error');
        return;
      }
      tier.uncertain = true;
      this.message(tier, this.strings.unconfirmed, 'error', true);
      this.cartChanged = true;
      this.refreshCartCount();
    }

    // #100 An item of this tier no longer exists on the Online Store (see header).
    // Busy state and lock stay while the basket is read; then the tier is locked.
    async itemsChanged(tier, plan, status) {
      console.warn(`${LOG} item no longer available`, (tier.data && tier.data.tier.handle) || '', status || '');
      const { button } = tier;
      const hadFocus = !!button && document.activeElement === button;
      const found = await this.basketHas(plan);
      const inBasket = found === 'all' || found === 'some';
      tier.uncertain = false;
      tier.locked = true;
      this.setBusy(tier, false);
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }
      const link = this.message(tier, this.strings.itemsChanged, 'error', inBasket);
      this.inFlight = false;
      if (inBasket) {
        this.cartChanged = true;
        this.refreshCartCount();
      }
      // A disabled button drops keyboard focus: hand it to "View basket", else to the message.
      if (!hadFocus) return;
      if (link) {
        link.focus();
        return;
      }
      const box = tier.el.querySelector('[data-setup-message]');
      if (box) {
        box.setAttribute('tabindex', '-1');
        box.focus();
      }
    }

    // Double-add fix: the basket has all (or some) of this tier's lines. Lock the
    // tier, as after a 422, and point to the basket.
    recovered(tier, plan, found) {
      const { button } = tier;
      const hadFocus = !!button && document.activeElement === button;
      const all = found === 'all';
      const skipped = plan.skipped || [];
      let text = this.strings.partialAdded;
      if (all) text = skipped.length ? `${this.strings.added} ${this.strings.skippedNote}` : this.strings.added;
      tier.uncertain = false;
      tier.locked = true;
      this.cartChanged = true;
      this.setBusy(tier, false);
      if (button) {
        if (all) this.setDone(tier, true);
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }
      const link = this.message(tier, text, all ? 'success' : 'error', true);
      this.inFlight = false;
      this.refreshCartCount();
      if (all) {
        const data = tier.data || {};
        tier.el.dispatchEvent(new CustomEvent('setup-builder:added', {
          bubbles: true,
          detail: {
            setup: data.setup ? data.setup.handle : null,
            tier: data.tier ? data.tier.handle : null,
            items: [],
            skipped,
            payload: plan.payload,
            recovered: true,
          },
        }));
      }
      // A disabled button drops keyboard focus; hand it to "View basket".
      if (hadFocus && link) link.focus();
    }

    // Double-add fix: are this tier's lines in the basket? GET cart.js and add up,
    // per payload variant, the quantities on lines tagged with this tier's
    // _tier_handle (and _setup_handle). Resolves 'all' | 'some' | 'none', or null
    // if the basket could not be read. Never rejects.
    basketHas(plan) {
      const props = (plan && plan.properties) || {};
      const lines = plan && plan.payload && Array.isArray(plan.payload.items) ? plan.payload.items : [];
      if (!props._tier_handle || !lines.length) return Promise.resolve(null);
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      let timer = null;
      return Promise.resolve()
        .then(() => {
          timer = controller ? setTimeout(() => controller.abort(), VERIFY_TIMEOUT_MS) : null;
          return fetch(`${this.cartUrl}.js`, {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            signal: controller ? controller.signal : undefined,
          });
        })
        .then((res) => (res && res.ok ? res.json() : null))
        .then((cart) => {
          if (!cart || !Array.isArray(cart.items)) return null;
          const mine = cart.items.filter((line) => line && line.properties
            && line.properties._tier_handle === props._tier_handle
            && (!props._setup_handle || line.properties._setup_handle === props._setup_handle));
          const have = lines.map((l) => mine
            .filter((line) => line.variant_id === l.id)
            .reduce((sum, line) => sum + (Number(line.quantity) || 0), 0));
          if (lines.every((l, i) => have[i] >= l.quantity)) return 'all';
          return have.some((n) => n > 0) ? 'some' : 'none';
        })
        .catch((err) => {
          console.info(`${LOG} basket not checked`, err);
          return null;
        })
        .then((found) => {
          if (timer) clearTimeout(timer);
          return found;
        });
    }

    // #95 Success (guide 8.4): event, then the post-add experience. See header.
    onSuccess(tier, plan, body) {
      const skipped = plan.skipped || [];
      const text = skipped.length ? `${this.strings.added} ${this.strings.skippedNote}` : this.strings.added;
      const data = tier.data || {};
      this.cartChanged = true;
      tier.el.dispatchEvent(new CustomEvent('setup-builder:added', {
        bubbles: true,
        detail: {
          setup: data.setup ? data.setup.handle : null,
          tier: data.tier ? data.tier.handle : null,
          items: body && Array.isArray(body.items) ? body.items : [],
          skipped,
          payload: plan.payload,
        },
      }));
      let stay = false;
      if (this.cartMode === 'auto') {
        try {
          stay = this.refreshCartUI(body) === true;
        } catch (err) {
          console.warn(`${LOG} cart refresh failed`, err);
        }
      }
      if (stay) {
        this.setBusy(tier, false);
        this.message(tier, text, 'success', true);
        this.inFlight = false;
        return;
      }
      // Go to the basket page. The button stays locked (aria-disabled, busy flag,
      // page lock) until the page changes, so it never flashes back to enabled.
      this.setDone(tier, true);
      this.message(tier, text, 'announce');
      this.goToBasket(tier, text);
    }

    // #97 Go to the basket page, with a fallback if it does not open (see header).
    goToBasket(tier, text) {
      this.stopNavTimer();
      const custom = parseInt(this.root.dataset.setupNavTimeout, 10);
      const wait = Number.isInteger(custom) && custom > 0 ? custom : NAVIGATE_FALLBACK_MS;
      this.navTimer = setTimeout(() => this.basketNotOpened(tier, text), wait);
      try {
        this.navigate(this.cartUrl);
      } catch (err) {
        console.warn(`${LOG} could not open the basket page`, err);
        this.basketNotOpened(tier, text);
      }
    }

    // #97 Still here: keep the "Added" button (no second add), show the success
    // text with a visible "View basket" link, release the lock, update the count.
    basketNotOpened(tier, text) {
      this.stopNavTimer();
      if (!tier.busy) return; // restored from the back/forward cache meanwhile
      this.message(tier, text, 'success', true);
      this.inFlight = false;
      this.refreshCartCount();
    }

    stopNavTimer() {
      if (this.navTimer) clearTimeout(this.navTimer);
      this.navTimer = null;
    }

    // #96 The theme's Dawn cart drawer / notification, if it renders one and
    // it has the methods the theme's product form uses; otherwise null.
    themeCartUI() {
      const el = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      return el && typeof el.renderContents === 'function' && typeof el.getSectionsToRender === 'function' ? el : null;
    }

    // #96 Add request body: in cart mode "auto" with a drawer / notification,
    // also ask for the sections it renders (bundled Section Rendering).
    requestBody(payload) {
      const ui = this.cartMode === 'auto' ? this.themeCartUI() : null;
      if (!ui) return payload;
      let ids = [];
      try {
        ids = Array.from(new Set((ui.getSectionsToRender() || []).map((s) => s && (s.section || s.id)).filter((id) => typeof id === 'string' && id)));
      } catch (err) {
        ids = [];
      }
      return ids.length ? { ...payload, sections: ids, sections_url: window.location.pathname } : payload;
    }

    // #96 Drawer / notification refresh (cart mode "auto"; see header). True only
    // if the theme's element took the response; false sends the shopper to the
    // basket page.
    refreshCartUI(body) {
      const ui = this.themeCartUI();
      const sections = body && body.sections;
      if (!ui || !sections || typeof sections !== 'object') return false;
      const first = (Array.isArray(body.items) && body.items[0]) || {};
      if (typeof ui.setActiveElement === 'function') ui.setActiveElement(document.activeElement);
      ui.classList.remove('is-empty');
      ui.renderContents({ ...body, id: first.id, key: first.key, sections });
      return true;
    }

    // #96 Header cart count from the theme's cart-icon-bubble section (see
    // header). Resolves true if the header was updated; never rejects.
    refreshCartCount() {
      const target = document.getElementById(CART_COUNT_SECTION);
      if (!target) return Promise.resolve(false);
      if (this.countRequest) return this.countRequest;
      const url = `${window.location.pathname}?sections=${CART_COUNT_SECTION}`;
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      let timer = null;
      this.countRequest = Promise.resolve()
        .then(() => {
          timer = controller ? setTimeout(() => controller.abort(), COUNT_TIMEOUT_MS) : null;
          return fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: controller ? controller.signal : undefined,
          });
        })
        .then((res) => (res && res.ok ? res.json() : null))
        .then((data) => !!data && this.applyCartCount(target, data[CART_COUNT_SECTION]))
        .catch((err) => {
          console.info(`${LOG} cart count not refreshed`, err);
          return false;
        })
        .then((updated) => {
          if (timer) clearTimeout(timer);
          this.countRequest = null;
          return updated;
        });
      return this.countRequest;
    }

    // #96 Writes the count from the section HTML into the header, in the
    // header's own markup. Text only: nothing from the response is used as HTML.
    applyCartCount(target, html) {
      if (typeof html !== 'string') return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.getElementById(`shopify-section-${CART_COUNT_SECTION}`);
      if (!section) return false;
      const bubble = section.querySelector('.cart-count-bubble');
      if (!bubble) {
        target.textContent = ''; // empty basket: the header shows no bubble
        return true;
      }
      const countEl = bubble.querySelector('[aria-hidden="true"]');
      const count = countEl ? countEl.textContent.trim() : ''; // no number from 100 items, as the header
      if (!/^\d{0,2}$/.test(count)) return false;
      const labelEl = bubble.querySelector('.visually-hidden');
      const shown = document.createElement('span');
      shown.className = 'cart-count-bubble';
      shown.setAttribute('aria-hidden', 'true');
      shown.textContent = count;
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = labelEl ? labelEl.textContent.trim() : '';
      target.textContent = '';
      target.append(shown, label);
      return true;
    }

    // #95 "Added" state of a tier's button while the basket page loads: tick +
    // strings.addedLabel instead of the spinner, still aria-disabled and locked.
    setDone(tier, on) {
      const { button } = tier;
      if (!button) return;
      if (on) {
        button.removeAttribute('aria-busy');
        button.setAttribute('aria-disabled', 'true');
        button.dataset.setupDone = 'true';
        return;
      }
      delete button.dataset.setupDone;
    }

    // #94 Busy state of one tier's button. aria-disabled (not disabled) keeps
    // keyboard focus on the button; addTier ignores clicks while busy. CSS swaps
    // the label for the spinner and "Adding…" on [aria-busy="true"].
    setBusy(tier, on) {
      const { button } = tier;
      tier.busy = on;
      this.setToggles(tier, !on && !tier.locked);
      if (!button) return;
      if (on) {
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
        return;
      }
      button.removeAttribute('aria-busy');
      if (!button.disabled) button.removeAttribute('aria-disabled');
    }

    // #94 Back/forward cache: the page can come back exactly as it was left,
    // i.e. busy after a successful add. Clear the busy state and the lock; a tier
    // locked after a 422 stays locked.
    restore() {
      this.stopNavTimer();
      this.tiers.forEach((tier) => {
        if (!tier.busy) return;
        this.setDone(tier, false);
        this.setBusy(tier, false);
        this.message(tier, '', '');
      });
      this.inFlight = false;
      // #96 The header may show the count from before the add.
      if (this.cartChanged) this.refreshCartCount();
    }

    post(payload) {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
      return fetch(this.cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined,
      }).finally(() => {
        if (timer) clearTimeout(timer);
      });
    }

    // Shows the error, clears the busy state (#94) and releases the page lock.
    // After a 422 the basket has changed (partial add), so this tier stays
    // disabled; otherwise the shopper can try again. Network errors, timeouts
    // and 5xx go to unknownResult() first (double-add fix); the unknown branch
    // here is kept for any other caller.
    fail(tier, err, status, body) {
      console.warn(`${LOG} add failed`, (tier.data && tier.data.tier.handle) || '', status || '', err);
      const { button } = tier;
      const hadFocus = !!button && document.activeElement === button;
      const description = body && typeof body.description === 'string' ? body.description.trim() : '';
      const changed = status === 422;
      const unknown = !status || status >= 500;
      this.setBusy(tier, false);
      const link = this.message(tier, changed && description ? description : this.strings.error, 'error', changed || unknown);
      this.inFlight = false;
      // #96 The basket has (422) or may have (no answer / 5xx) changed: update the header count.
      if (changed || unknown) {
        this.cartChanged = true;
        this.refreshCartCount();
      }
      if (!button || !changed) return;
      tier.locked = true;
      this.setToggles(tier, false);
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      // A disabled button drops keyboard focus; hand it to "View basket", the next step.
      if (hadFocus && link) link.focus();
    }

    // Text only (never HTML); optional "View basket" link to the basket page,
    // returned so the caller can focus it. Type "busy" is for screen readers only.
    message(tier, text, type, basketLink) {
      const box = tier.el.querySelector('[data-setup-message]');
      if (!box) return null;
      box.textContent = text || '';
      let link = null;
      if (basketLink) {
        link = document.createElement('a');
        link.className = 'link setup-tier__basket-link';
        link.href = this.cartUrl;
        link.textContent = this.strings.viewBasket;
        box.append(' ', link);
      }
      if (type) box.dataset.type = type;
      else delete box.dataset.type;
      return link;
    }

    // Separate method so #97 (cart mode) and tests can change where the shopper goes.
    navigate(url) {
      window.location.assign(url);
    }

    // ---- BSS wholesale prices (see header) ----

    // Starts watching the BSS price nodes of wholesale tiers, if there are any.
    watchWholesalePrices() {
      const nodes = this.root.querySelectorAll('[data-setup-bss-price]');
      this.wholesaleTiers = Array.from(this.root.querySelectorAll('[data-setup-tier]')).filter((el) => el.querySelector('[data-setup-wholesale-price]'));
      if (!this.wholesaleTiers.length) return;
      this.pricesTimedOut = false;
      this.updateWholesalePrices();
      if (typeof MutationObserver === 'function') {
        let queued = false;
        this.priceObserver = new MutationObserver(() => {
          if (queued) return;
          queued = true;
          // Coalesce BSS's innerHTML + style writes into one update.
          Promise.resolve().then(() => {
            queued = false;
            this.updateWholesalePrices();
          });
        });
        nodes.forEach((node) => this.priceObserver.observe(node, { attributes: true, attributeFilter: ['style'], childList: true, characterData: true, subtree: true }));
      }
      const custom = parseInt(this.root.dataset.setupPriceTimeout, 10);
      const wait = Number.isInteger(custom) && custom > 0 ? custom : WHOLESALE_TIMEOUT_MS;
      this.priceTimer = setTimeout(() => {
        this.pricesTimedOut = true;
        this.updateWholesalePrices();
      }, wait);
    }

    // Recomputes every wholesale tier from what BSS has written so far.
    updateWholesalePrices() {
      (this.wholesaleTiers || []).forEach((el) => this.updateWholesaleTier(el));
    }

    updateWholesaleTier(el) {
      const box = el.querySelector('[data-setup-wholesale-total]');
      const format = box ? box.dataset.moneyFormat || '' : '';
      const currency = (() => {
        const tier = this.tiers.find((t) => t.el === el);
        return tier && tier.ok ? tier.data.currency : null;
      })();
      // Work everything out first, then change the page in one go.
      const rows = Array.from(el.querySelectorAll('.setup-item[data-item-status]'), (row) => {
        const wrap = row.querySelector('[data-setup-wholesale-price]:not([hidden])');
        const quantity = parseInt(row.dataset.quantity, 10) || 1;
        const price = wrap ? readBssPrice(wrap.querySelector('[data-setup-bss-price]'), format) : { done: false, unit: null };
        const toggle = row.querySelector('input[data-setup-optional]');
        return {
          wrap,
          quantity,
          price,
          // Oct 2026: unticked optional rows are not in the total.
          counts: ADDABLE.has(row.dataset.itemStatus) && !(toggle && !toggle.checked),
          required: row.dataset.required !== 'false',
        };
      }).filter((r) => r.wrap);
      const counted = rows.filter((r) => r.counts);
      const complete = counted.length > 0 && counted.every((r) => r.price.done);
      const anyShown = rows.some((r) => r.price.done);
      let total = 0;
      let required = 0;
      if (complete) {
        counted.forEach((r) => {
          const line = r.price.unit * r.quantity;
          total += line;
          if (r.required) required += line;
        });
      }

      rows.forEach((r) => {
        const line = r.wrap.querySelector('[data-setup-line-price]');
        // SUP-32: the line total and "each" wording only when more than one.
        const each = r.wrap.querySelector('[data-setup-each]');
        if (line) line.hidden = r.quantity < 2;
        if (each) {
          each.classList.toggle('setup-item__each--single', r.quantity < 2);
          each.querySelectorAll('[data-setup-each-text]').forEach((t) => { t.hidden = r.quantity < 2; });
        }
        if (r.price.done) {
          if (line) line.textContent = formatMoney(r.price.unit * r.quantity, format, currency);
          r.wrap.dataset.setupPriceState = 'ready';
        } else {
          if (line) line.textContent = '';
          r.wrap.dataset.setupPriceState = 'pending';
        }
      });
      const note = el.querySelector('[data-setup-wholesale-note]');
      const vat = el.querySelector('[data-setup-wholesale-vat]');
      if (box) {
        const totalEl = box.querySelector('[data-setup-total]');
        const requiredEl = box.querySelector('[data-setup-required-total]');
        if (totalEl) totalEl.textContent = complete ? formatMoney(total, format, currency) : '';
        if (requiredEl) requiredEl.textContent = complete ? formatMoney(required, format, currency) : '';
        // "Without optional extras" only while an optional item is included.
        const alt = box.querySelector('.setup-tier__subtotal-alt');
        if (alt) alt.hidden = !counted.some((r) => !r.required);
        box.hidden = !complete;
      }
      if (note) note.hidden = complete;
      if (vat) vat.hidden = !anyShown;
      let state = 'pending';
      if (complete) state = 'ready';
      else if (this.pricesTimedOut) state = 'fallback';
      if (el.dataset.setupPrices !== state && state === 'fallback') {
        console.info(`${LOG} wholesale prices not complete; keeping the basket note`, el.dataset.tierHandle || '');
      }
      el.dataset.setupPrices = state;
    }

    // Figma redesign: ticked popular extras join a ready plan (see header).
    // Returns the plan unchanged when nothing valid is ticked.
    withExtras(plan) {
      if (!plan || plan.status !== 'ready' || !plan.payload) return plan;
      const ids = Array.from(this.root.querySelectorAll('input[data-setup-extra]'))
        .filter((el) => el.checked && !el.disabled)
        .map((el) => parseInt(el.dataset.variantId, 10))
        .filter((id) => isId(id));
      if (!ids.length) return plan;
      const items = plan.payload.items.map((line) => ({ ...line, properties: { ...line.properties } }));
      const extras = [];
      ids.forEach((id) => {
        extras.push({ variantId: id, quantity: 1 });
        const line = items.find((l) => l.id === id);
        if (line) line.quantity += 1;
        else items.push({ id, quantity: 1, properties: { ...plan.properties } });
      });
      return { ...plan, payload: { ...plan.payload, items }, extras };
    }

    // Lookup helper for later tickets and console checks.
    getTier(handle) {
      return this.tiers.find((t) => t.ok && t.data.tier.handle === handle) || null;
    }

    // #91 Payload plan for a tier (handle, tier object or tier element). #92 calls
    // this when the button is pressed, so it always reflects the rendered data.
    planTier(ref) {
      let tier = ref;
      if (typeof ref === 'string') tier = this.getTier(ref);
      else if (ref && ref.nodeType === 1) tier = this.tiers.find((t) => t.el === ref) || null;
      if (tier) this.readToggles(tier);
      return planTier(tier);
    }
  }

  // Initialise every root once; again for sections re-rendered in the theme editor.
  const init = (scope) => {
    (scope || document).querySelectorAll('[data-setup-builder]').forEach((el) => {
      if (el.setupBuilder) return;
      try {
        el.setupBuilder = new SetupBuilder(el);
      } catch (err) {
        el.dataset.setupState = 'error';
        console.warn(`${LOG} init failed`, err);
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  // #97 Page is being left (basket page opening): stop the fallback timer, so it
  // cannot fire if this page is kept in the back/forward cache.
  window.addEventListener('pagehide', () => {
    document.querySelectorAll('[data-setup-builder]').forEach((el) => {
      if (el.setupBuilder) el.setupBuilder.stopNavTimer();
    });
  });
  // #94 Page restored from the back/forward cache: clear any busy state.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-setup-builder]').forEach((el) => {
      if (el.setupBuilder) el.setupBuilder.restore();
    });
  });
})();
